import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Lock, Unlink, ZoomIn, ZoomOut, Maximize, SlidersHorizontal, Check } from "lucide-react";
import { useStudio, useLookups, useCatalogModels } from "@/store/StudioContext";
import { EmptyState, DemoBadge } from "@/components/common/States";
import MediaFrame from "@/components/common/MediaFrame";
import PendingDialog from "@/components/common/PendingDialog";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { hasInputs, hasOutput } from "@/domain/canvasRules";
import { NODE_TYPES } from "@/domain/entities";
import { resolveNodeAsset } from "@/domain/canvasAssetResolution";
import { isConnectionOperational, syncConnectionsWithCatalog } from "@/domain/connections";
import { isJobCompleted, isJobFailed } from "@/domain/jobResults";
import { api } from "@/lib/api";
import { PromptMentionEditor } from "@/components/common/PromptMentionEditor";
import { VideoPreflightModal } from "@/components/project/VideoPreflightModal";
import NodeConfigModal from "@/components/project/NodeConfigModal";
// Inline fallback UUID v4 generator (no external dependency)
import { t } from "@/i18n";
const uuidv4 = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15));

const NODE_W = 230;
const PORT_Y = 20;
const TYPE_COLOR = { text: "#a3a3a3", reference: "#8fb4cb", image: "#f5b544", video: "#cb8fb9" };

// El catálogo del backend usa model_id / provider_id; normaliza id y provider para los selectores.
const normalizeModel = (m) => ({ ...m, id: m.model_id ?? m.id, provider: m.provider_id ?? m.provider });
const modelLabel = (m) => {
  if (m.pending_integration || m.pendingIntegration) {
    return `${m.label || m.id} (pendiente)`;
  }
  const isPricePending = (m.price_pending === true || m.pricePending === true) || (m.generation_available === false && !m.pending_integration && !m.pendingIntegration);
  if (isPricePending) {
    return `${m.label || m.id} — Precio pendiente — generación no disponible`;
  }
  return m.label || m.id;
};

const renderModelOptions = (models) => {
  const available = [];
  const pricePending = [];
  const pending = [];

  for (const m of models) {
    const isPending = m.pending_integration || m.pendingIntegration;
    const isPricePending = !isPending && (m.price_pending === true || m.pricePending === true || (m.generation_available === false && !isPending));
    if (isPending) {
      pending.push(m);
    } else if (isPricePending) {
      pricePending.push(m);
    } else {
      available.push(m);
    }
  }

  return (
    <>
      {available.length > 0 && (
        <optgroup label="Disponibles">
          {available.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label || m.id}
            </option>
          ))}
        </optgroup>
      )}
      {pricePending.length > 0 && (
        <optgroup label="Integrados con precio pendiente">
          {pricePending.map((m) => (
            <option key={m.id} value={m.id} disabled>
              {m.label || m.id} — Precio pendiente — generación no disponible
            </option>
          ))}
        </optgroup>
      )}
      {pending.length > 0 && (
        <optgroup label="Pendientes de integración">
          {pending.map((m) => (
            <option key={m.id} value={m.id} disabled>
              {m.label || m.id} [Pendiente de integración]
            </option>
          ))}
        </optgroup>
      )}
    </>
  );
};

const edgePath = (a, b) => {
  const x1 = a.x + NODE_W, y1 = a.y + PORT_Y, x2 = b.x, y2 = b.y + PORT_Y;
  const dx = Math.max(40, Math.abs(x2 - x1) / 2);
  return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
};

const NodeBody = ({ node, data, onRun, hasOperational, hasConnection, onOpenConfig, onAssignScene }) => {
  const { state, dispatch } = useStudio();
  const { assetById, referenceById } = useLookups();
  // Load raw catalog models per kind (provider capabilities) then filter by actual modelKind field
  const rawImageModels = useCatalogModels('image');
  const rawVideoModels = useCatalogModels('video');
  // Preserve models that explicitly declare their kind. Keep models with multiple capabilities if they report the correct kind.
  const imageModels = rawImageModels.filter((m) => (m.model_kind || '').toLowerCase() === 'image').map(normalizeModel);
  const videoModels = rawVideoModels.filter((m) => (m.model_kind || '').toLowerCase() === 'video').map(normalizeModel);
  const update = (d) => dispatch({ type: "NODE_UPDATE", payload: { id: node.id, data: d } });
  const scene = data.scenes.find((s) => s.id === node.data.sceneId);

  if (node.type === "text") {
    // Buscar si este nodo de texto está conectado a un nodo de vídeo para resolver referencias
    const targetVideoEdge = data.edges.find((e) => e.sourceId === node.id && data.nodes.find((x) => x.id === e.targetId)?.type === "video");
    const targetVideoNode = targetVideoEdge ? data.nodes.find((x) => x.id === targetVideoEdge.targetId) : null;
    let textStartAsset = null;
    let textEndAsset = null;
    if (targetVideoNode) {
      const vidImgEdges = data.edges.filter((e) => e.targetId === targetVideoNode.id && data.nodes.find((x) => x.id === e.sourceId)?.type === "image");
      const startEdge = vidImgEdges.find((e) => e.role === "start_frame") || vidImgEdges[0];
      const endEdge = vidImgEdges.find((e) => e.role === "end_frame") || (vidImgEdges.length > 1 ? vidImgEdges[1] : null);
      if (startEdge) textStartAsset = resolveNodeAsset(data.nodes.find((x) => x.id === startEdge.sourceId), data, assetById);
      if (endEdge) textEndAsset = resolveNodeAsset(data.nodes.find((x) => x.id === endEdge.sourceId), data, assetById);
    }
    return (
      <div className="flex flex-col gap-1.5" onPointerDown={(e) => e.stopPropagation()}>
        <PromptMentionEditor
          value={node.data.text || ""}
          onChange={(val) => update({ text: val })}
          placeholder={t.canvas.textPh}
          rows={3}
          testId={`node-text-${node.id}`}
          startAsset={textStartAsset}
          endAsset={textEndAsset}
          supportsFLF={Boolean(textEndAsset)}
          characters={data.characters || []}
          maxChars={targetVideoNode ? (targetVideoNode?.data?.modelId?.startsWith("kling") ? 2500 : 5000) : null}
          modelLabel={targetVideoNode?.data?.modelId || ""}
        />
      </div>
    );
  }
  if (node.type === "reference") {
    const ref = referenceById[node.data.referenceId];
    return (
      <div className="flex flex-col gap-2">
        <MediaFrame asset={assetById[ref?.assetId]} className="aspect-video" fit="contain" emptyMessage={t.canvas?.pickReference || "Sin referencia"} />
        <select data-testid={`node-ref-select-${node.id}`} className="v-input h-8 text-xs" value={node.data.referenceId || ""} onChange={(e) => update({ referenceId: e.target.value || null })} onPointerDown={(e) => e.stopPropagation()}>
          <option value="">{t.canvas.pickReference}</option>
          {state.references.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
        </select>
      </div>
    );
  }

  const asset = resolveNodeAsset(node, data, assetById);

  // Verificación de entradas de vídeo
  const vidImgEdges = node.type === "video" ? data.edges.filter((e) => e.targetId === node.id && data.nodes.find((x) => x.id === e.sourceId)?.type === "image") : [];
  const startEdge = vidImgEdges.find((e) => e.role === "start_frame") || vidImgEdges[0];
  const endEdge = vidImgEdges.find((e) => e.role === "end_frame") || (vidImgEdges.length > 1 ? vidImgEdges[1] : null);
  const startSourceNode = startEdge ? data.nodes.find((x) => x.id === startEdge.sourceId) : null;
  const startAssetResolved = startSourceNode ? resolveNodeAsset(startSourceNode, data, assetById) : null;
  const endAssetResolved = endEdge ? resolveNodeAsset(data.nodes.find((x) => x.id === endEdge.sourceId), data, assetById) : null;

  const isImageConnectedButEmpty = Boolean(startSourceNode && !startAssetResolved);
  const sceneActiveAsset = scene?.selectedAssetId ? assetById[scene.selectedAssetId] : null;
  const sceneHasActiveImage = Boolean(!startSourceNode && sceneActiveAsset && sceneActiveAsset.type === "image");

  return (
    <div className="flex flex-col gap-2">
      <MediaFrame
        asset={asset}
        className="aspect-video"
        fit="contain"
        emptyMessage={node.type === "image" ? (t.canvas?.noAsset || "Sin imagen asignada") : (t.canvas?.noVideo || "Sin vídeo generado")}
      />

      {/* Variantes y asignación a la toma vinculada */}
      {asset && scene && (
        <div className="mt-0.5">
          {node.type === "video" && asset.type !== "video" ? (
            <div
              data-testid={`node-video-input-badge-${node.id}`}
              className="flex items-center justify-center gap-1 rounded bg-sky-950/60 border border-sky-500/40 py-0.5 px-1.5 text-[10px] font-medium text-sky-300"
            >
              Imagen de entrada de la toma
            </div>
          ) : scene.selectedAssetId === asset.id ? (
            <div data-testid={`node-assigned-badge-${node.id}`} className="flex items-center justify-center gap-1 rounded bg-emerald-500/10 border border-emerald-500/30 py-0.5 px-1.5 text-[10px] font-medium text-emerald-400">
              <Check size={11} /> Asignado a esta toma
            </div>
          ) : (
            <button
              data-testid={`node-assign-scene-${node.id}`}
              type="button"
              className="v-btn-primary v-btn-sm w-full justify-center text-[10px] h-7"
              onClick={(e) => {
                e.stopPropagation();
                if (onAssignScene) onAssignScene(node, asset, scene);
              }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              Asignar a esta toma
            </button>
          )}
        </div>
      )}

      <select data-testid={`node-scene-select-${node.id}`} className="v-input h-8 text-xs" value={node.data.sceneId || ""} onChange={(e) => update({ sceneId: e.target.value || null })} onPointerDown={(e) => e.stopPropagation()}>
        <option value="">{t.canvas.unlinked}</option>
        {data.scenes.map((s, i) => <option key={s.id} value={s.id}>{`${i + 1}. ${s.title || t.common.untitled}`}</option>)}
      </select>
        {node.type === "image" && (
          <select data-testid={`node-model-select-${node.id}`} className="v-input h-8 text-xs mt-1" value={node.data.model_id || ""} onChange={(e) => update({ model_id: e.target.value || null })} onPointerDown={(e) => e.stopPropagation()}>
            <option value="">Select model</option>
            {renderModelOptions(imageModels)}
            {node.data.model_id && !imageModels.find((m) => m.id === node.data.model_id) && (
              <div className="mt-1 text-xs text-red-400">{t.canvas.modelUnavailable || 'Modelo seleccionado no disponible'}</div>
            )}
          </select>
        )}
        {node.type === "video" && (
          <>
            <select data-testid={`node-model-select-${node.id}`} className="v-input h-8 text-xs mt-1" value={node.data.model_id || ""} onChange={(e) => update({ model_id: e.target.value || null })} onPointerDown={(e) => e.stopPropagation()}>
              <option value="">Select model</option>
              {renderModelOptions(videoModels)}
              {node.data.model_id && !videoModels.find((m) => m.id === node.data.model_id) && (
                <div className="mt-1 text-xs text-red-400">{t.canvas.modelUnavailable || 'Modelo seleccionado no disponible'}</div>
              )}
            </select>
            {node.type === "video" && (
              <div data-testid={`node-video-frames-${node.id}`} className="mt-1 flex flex-col gap-1 rounded bg-[#101010] p-1.5 text-[10px]">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[#737373]">Start Frame:</span>
                  {startAssetResolved ? (
                    <span className="inline-flex items-center gap-1 font-medium text-emerald-400 truncate max-w-[120px]">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                      {startAssetResolved.label || startAssetResolved.filename || "Vinculado"}
                    </span>
                  ) : isImageConnectedButEmpty ? (
                    <span data-testid={`node-video-input-missing-${node.id}`} className="font-medium text-amber-400 truncate max-w-[130px]">
                      ⚠️ Falta imagen (nodo sin resultado)
                    </span>
                  ) : sceneHasActiveImage ? (
                    <span className="inline-flex items-center gap-1 font-medium text-emerald-400 truncate max-w-[120px]" title="Imagen activa de la toma">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                      Toma: {sceneActiveAsset.label || "Imagen de toma"}
                    </span>
                  ) : (
                    <span className="text-amber-400/90 font-mono">Sin conectar (T2V)</span>
                  )}
                </div>
                {endAssetResolved && (
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[#737373]">End Frame:</span>
                    <span className="inline-flex items-center gap-1 font-medium text-emerald-400 truncate max-w-[120px]">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                      {endAssetResolved.label || endAssetResolved.filename || "Vinculado"}
                    </span>
                  </div>
                )}
                {isImageConnectedButEmpty && (
                  <div className="mt-0.5 text-[9px] text-amber-400/90 leading-tight">
                    Falta imagen de entrada: genera primero el nodo Imagen conectado.
                  </div>
                )}
              </div>
            )}
            {node.data.model_id && (() => {
              const selectedModel = videoModels.find((m) => m.id === node.data.model_id);
              if (!selectedModel) return null;
              if (selectedModel.id === "kling-v3") {
                return (
                  <div data-testid={`node-budget-reserve-${node.id}`} className="mt-1 flex flex-col gap-0.5 rounded bg-[#101010]/80 p-1.5 text-[10px] text-[#a3a3a3]">
                    <div className="flex justify-between font-mono">
                      <span>Retención est. de saldo:</span>
                      <span className="text-[#f5b544] font-semibold">$0.4032 USD</span>
                    </div>
                    <span className="text-[9px] text-[#737373]">
                      Estimación prov. (5s std + 20% margen). No constituye precio final ni tope de facturación garantizado.
                    </span>
                  </div>
                );
              }
              if (selectedModel.id === "wan2.7") {
                return (
                  <div data-testid={`node-budget-reserve-${node.id}`} className="mt-1 flex justify-between rounded bg-[#101010]/80 p-1.5 text-[10px] text-[#a3a3a3] font-mono">
                    <span>Reserva:</span>
                    <span className="text-[#f5b544] font-semibold">$0.50 USD</span>
                  </div>
                );
              }
              if (selectedModel.rate_per_second_usd || selectedModel.ratePerSecondUsd || selectedModel.reserve_usd || selectedModel.reserveUsd) {
                const dur = selectedModel.default_duration || selectedModel.defaultDuration || (selectedModel.durations?.[0] ?? 5);
                const rate = selectedModel.rate_per_second_usd ?? selectedModel.ratePerSecondUsd;
                const margin = selectedModel.safety_margin_pct ?? selectedModel.safetyMarginPct ?? 20;
                const reserve = (rate != null)
                  ? (dur * rate * (1 + margin / 100))
                  : (selectedModel.reserve_usd ?? selectedModel.reserveUsd ?? 0.50);
                return (
                  <div data-testid={`node-budget-reserve-${node.id}`} className="mt-1 flex flex-col gap-0.5 rounded bg-[#101010]/80 p-1.5 text-[10px] text-[#a3a3a3]">
                    <div className="flex justify-between font-mono">
                      <span>Retención est. de saldo:</span>
                      <span className="text-[#f5b544] font-semibold">${Number(reserve).toFixed(4)} USD</span>
                    </div>
                    <span className="text-[9px] text-[#737373]">
                      Estimación prov. ({dur}s + {margin}% margen). No constituye precio final ni tope de facturación garantizado.
                    </span>
                  </div>
                );
              }
              return null;
            })()}
          </>
        )}
      
      <div className="flex gap-1.5 mt-1">
        <button
          data-testid={`node-config-btn-${node.id}`}
          type="button"
          className="v-btn-ghost v-btn-sm flex-1 justify-center text-[10px] h-7"
          onClick={(e) => {
            e.stopPropagation();
            if (onOpenConfig) onOpenConfig();
          }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <SlidersHorizontal size={11} /> Configurar
        </button>
        <button
          data-testid={`node-run-${node.id}`}
          className="v-btn-ghost v-btn-sm flex-1 justify-center text-[10px] h-7"
          disabled={!hasOperational || !node.data?.model_id || isImageConnectedButEmpty}
          title={
            !node.data?.model_id
              ? "Selecciona un modelo para generar"
              : isImageConnectedButEmpty
              ? "Falta la imagen de entrada: el nodo Imagen aún no tiene resultado"
              : hasOperational
              ? t.canvas.runHint
              : (hasConnection ? t.connections.noneOperational : t.connections.required)
          }
          onClick={onRun}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Lock size={11} />{t.canvas.run}
        </button>
      </div>
    </div>
  );
};

const CanvasView = ({ data }) => {
  const { state, dispatch, catalog, submitGeneration, submitUpscale } = useStudio();
  const { assetById, referenceById } = useLookups();
  const imageModels = useCatalogModels('image').filter((m) => (m.model_kind || '').toLowerCase() === 'image').map(normalizeModel);
  const videoModels = useCatalogModels('video').filter((m) => (m.model_kind || '').toLowerCase() === 'video').map(normalizeModel);
  const { project, nodes, edges, selection } = data;
  const wrapRef = useRef(null);
  const [view, setView] = useState({ x: 40, y: 40, k: 1 });
  const [drag, setDrag] = useState(null); // {id, sx, sy, ox, oy, dx, dy}
  const [pan, setPan] = useState(null);
  const [link, setLink] = useState(null); // {sourceId, x, y}
  const [pending, setPending] = useState(false);
  const [pendingNodeId, setPendingNodeId] = useState(null);
  const [pendingJobId, setPendingJobId] = useState(null);
  const [canvasPreflight, setCanvasPreflight] = useState(null);
  const [configNodeId, setConfigNodeId] = useState(null);
  const [replaceTarget, setReplaceTarget] = useState(null);

  // Sincronización explícita cuando se navega desde Storyboard con toma seleccionada
  useEffect(() => {
    if (selection?.sceneId && !selection?.nodeId) {
      const match = nodes.find((n) => n.data?.sceneId === selection.sceneId);
      if (match) {
        dispatch({
          type: "SELECT",
          payload: {
            projectId: project.id,
            patch: { nodeId: match.id, sceneId: selection.sceneId, edgeId: null },
          },
        });
      }
    }
  }, [selection?.sceneId, selection?.nodeId, nodes, dispatch, project.id]);

  useEffect(() => {
    if (state.lastError) {
      toast.error(state.lastError.message);
      dispatch({ type: "CLEAR_ERROR" });
    }
  }, [state.lastError, dispatch]);

  // Clear pending when the specific job created for this node finishes (completed or failed)
  useEffect(() => {
    if (pending && pendingJobId) {
      const job = state.jobs.find((j) => j.job_id === pendingJobId && (isJobCompleted(j) || isJobFailed(j)));
      if (job) {
        setPending(false);
        setPendingNodeId(null);
        setPendingJobId(null);
      }
    }
  }, [state.jobs, pending, pendingJobId]);

  const toCanvas = useCallback((clientX, clientY) => {
    const r = wrapRef.current.getBoundingClientRect();
    return { x: (clientX - r.left - view.x) / view.k, y: (clientY - r.top - view.y) / view.k };
  }, [view]);

  const selectNode = (n) => {
    const nodeLatest = data.assets
      .filter((a) => a.nodeId === n.id)
      .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""))[0];
    const sceneSel = n.data.sceneId ? data.scenes.find((s) => s.id === n.data.sceneId)?.selectedAssetId ?? null : null;
    dispatch({ type: "SELECT", payload: { projectId: project.id, patch: { nodeId: n.id, edgeId: null, ...(n.data.sceneId ? { sceneId: n.data.sceneId, assetId: nodeLatest?.id ?? sceneSel } : {}) } } });
  };

  const onWindowMove = useCallback((e) => {
    if (drag) setDrag((d) => ({ ...d, dx: (e.clientX - d.sx) / view.k, dy: (e.clientY - d.sy) / view.k }));
    else if (pan) setView((v) => ({ ...v, x: pan.ox + e.clientX - pan.sx, y: pan.oy + e.clientY - pan.sy }));
    else if (link) setLink((l) => ({ ...l, ...toCanvas(e.clientX, e.clientY) }));
  }, [drag, pan, link, view.k, toCanvas]);

  const onWindowUp = useCallback((e) => {
    if (drag) { dispatch({ type: "NODE_MOVE", payload: { id: drag.id, x: Math.round(drag.ox + drag.dx), y: Math.round(drag.oy + drag.dy) } }); setDrag(null); }
    if (pan) setPan(null);
    if (link) {
      const target = document.elementFromPoint(e.clientX, e.clientY)?.closest("[data-port-in]");
      if (target) {
        const targetId = target.getAttribute("data-port-in");
        const targetNode = nodes.find((x) => x.id === targetId);
        const sourceNode = nodes.find((x) => x.id === link.sourceId);
        let role = null;
        if (targetNode?.type === "video" && sourceNode?.type === "image") {
          const existingImageEdges = edges.filter(
            (ed) => ed.targetId === targetId && nodes.find((x) => x.id === ed.sourceId)?.type === "image"
          );
          const hasStart = existingImageEdges.some((ed) => ed.role === "start_frame");
          role = hasStart ? "end_frame" : "start_frame";
        }
        dispatch({
          type: "EDGE_ADD",
          payload: { projectId: project.id, sourceId: link.sourceId, targetId, role },
        });
      }
      setLink(null);
    }
  }, [drag, pan, link, dispatch, project.id, nodes, edges]);

  useEffect(() => {
    if (!drag && !pan && !link) return undefined;
    window.addEventListener("pointermove", onWindowMove);
    window.addEventListener("pointerup", onWindowUp);
    return () => { window.removeEventListener("pointermove", onWindowMove); window.removeEventListener("pointerup", onWindowUp); };
  }, [drag, pan, link, onWindowMove, onWindowUp]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) return;
      if (selection.edgeId) dispatch({ type: "EDGE_DELETE", payload: { id: selection.edgeId } });
      else if (selection.nodeId) dispatch({ type: "NODE_DELETE", payload: { id: selection.nodeId } });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selection.edgeId, selection.nodeId, dispatch]);

  const addNode = (type) => {
    const r = wrapRef.current.getBoundingClientRect();
    const c = toCanvas(r.left + r.width / 2, r.top + r.height / 2);
    dispatch({ type: "NODE_ADD", payload: { projectId: project.id, type, x: Math.round(c.x - NODE_W / 2 + (Math.random() * 60 - 30)), y: Math.round(c.y - 60 + (Math.random() * 60 - 30)), data: type === "image" && selection.sceneId ? { sceneId: selection.sceneId } : {} } });
  };
  const allConnections = syncConnectionsWithCatalog(state.connections, catalog);
  const hasConnection = allConnections.length > 0;
  const hasOperational = allConnections.some(isConnectionOperational);

  // Ejecuta una generación real mediante la API del backend.
  const run = async (n) => {
    if (!hasOperational) return;
    // Ensure a model is selected for this node
    if (!n.data.model_id) {
      toast.error(t.generation?.noModel || "Selecciona un modelo antes de ejecutar");
      return;
    }
    const catalogModels = n.type === "image" ? imageModels : videoModels;
    const model = catalogModels.find((m) => m.id === n.data.model_id);
    if (!model) {
      toast.error(t.generation?.noModel || "Modelo seleccionado no es compatible");
      return;
    }
    const selectedModel = model;
    const selectedConn = allConnections.find((c) => c.provider === (model.provider || "apimart") && isConnectionOperational(c)) || allConnections.find(isConnectionOperational);
    const providerId = model.provider || selectedConn?.provider || "apimart";
    if (model.pending_integration || model.pendingIntegration) {
      toast.error(`${model.label || model.id}: integración pendiente, no se puede generar.`);
      return;
    }
    const isPricePending = model.price_pending === true || model.pricePending === true || (model.generation_available === false && !model.pending_integration && !model.pendingIntegration);
    if (isPricePending) {
      toast.error(`${model.label || model.id}: Precio pendiente — generación no disponible.`);
      return;
    }
    const modelId = model.id;
    // Protección de primera generación real para Kling 3:
    // Requiere autorización activa de un solo uso en backend
    if (modelId === "kling-v3") {
      try {
        const authStatus = await api.getKlingAuthStatus();
        if (!authStatus?.authorized || authStatus?.consumed) {
          toast.error("Kling 3 requiere autorización de un solo uso activa. No disponible o ya consumida.");
          return;
        }
      } catch {
        toast.error("No se pudo verificar la autorización de un solo uso de Kling 3.");
        return;
      }
    }

    const scene = data.scenes.find((s) => s.id === n.data.sceneId);
    const inputs = edges
      .filter((e) => e.targetId === n.id)
      .map((e) => nodes.find((x) => x.id === e.sourceId))
      .filter(Boolean);
    const prompt = [scene?.prompt, ...inputs.filter((i) => i.type === "text").map((i) => i.data.text)]
      .filter(Boolean)
      .join("\n");

    // Determinar fotogramas para vídeo (T2V, I2V o Start+End Frame)
    let sourceAssetId = null;
    let endAssetId = null;
    let videoMode = "image_to_video";
    const supportedModes = model?.modes || [];
    const supportsT2V = supportedModes.includes("text_to_video");
    const supportsFLF = supportedModes.includes("first_last_frame");

    const resolveAssetId = (imgNode) => {
      const resolved = resolveNodeAsset(imgNode, data, assetById);
      return resolved?.id || null;
    };

    if (n.type === "video") {
      const imageEdges = edges.filter(
        (e) => e.targetId === n.id && nodes.find((x) => x.id === e.sourceId)?.type === "image"
      );

      if (imageEdges.length >= 2) {
        // Encontrar conexiones por rol explícito (start_frame y end_frame)
        let startEdge = imageEdges.find((e) => e.role === "start_frame");
        let endEdge = imageEdges.find((e) => e.role === "end_frame");

        // Estrategia de compatibilidad segura para conexiones existentes sin rol:
        // Si no tienen rol explícito asignado, migrar deterministamente por fecha inmutable de creación (createdAt),
        // NUNCA por coordenadas espaciales, y persistir los roles explícitos en el estado.
        if (!startEdge && !endEdge) {
          const sortedByCreation = [...imageEdges].sort((a, b) => (new Date(a.createdAt || 0)) - (new Date(b.createdAt || 0)));
          startEdge = sortedByCreation[0];
          endEdge = sortedByCreation[1];
          dispatch({ type: "EDGE_UPDATE", payload: { id: startEdge.id, patch: { role: "start_frame" } } });
          dispatch({ type: "EDGE_UPDATE", payload: { id: endEdge.id, patch: { role: "end_frame" } } });
          toast.info("Conexiones migradas con roles explícitos: primera creada como Start Frame y segunda como End Frame.");
        } else if (startEdge && !endEdge) {
          endEdge = imageEdges.find((e) => e.id !== startEdge.id);
          if (endEdge) {
            dispatch({ type: "EDGE_UPDATE", payload: { id: endEdge.id, patch: { role: "end_frame" } } });
          }
        } else if (!startEdge && endEdge) {
          startEdge = imageEdges.find((e) => e.id !== endEdge.id);
          if (startEdge) {
            dispatch({ type: "EDGE_UPDATE", payload: { id: startEdge.id, patch: { role: "start_frame" } } });
          }
        }

        // Si ambas conexiones tienen asignado el mismo rol, detener y solicitar desambiguación explícita
        if (startEdge && endEdge && startEdge.id === endEdge.id) {
          toast.error("Configuración ambigua: selecciona cada conexión para definir cuál es Start Frame y cuál es End Frame.");
          return;
        }

        sourceAssetId = resolveAssetId(nodes.find((x) => x.id === startEdge?.sourceId));
        endAssetId = resolveAssetId(nodes.find((x) => x.id === endEdge?.sourceId));

        if (!supportsFLF) {
          toast.error(`El modelo ${model?.label || modelId} no admite fotograma inicial y final (first_last_frame).`);
          return;
        }
        if (!sourceAssetId || !endAssetId) {
          toast.error("No se pudieron resolver ambos fotogramas (inicial y final) para la generación de vídeo.");
          return;
        }
        videoMode = "first_last_frame";
      } else if (imageEdges.length === 1) {
        // Conexión única: compatibilidad 100% con proyectos existentes (Image-to-Video)
        const edge = imageEdges[0];
        if (!edge.role) {
          dispatch({ type: "EDGE_UPDATE", payload: { id: edge.id, patch: { role: "start_frame" } } });
        }
        sourceAssetId = resolveAssetId(nodes.find((x) => x.id === edge.sourceId));
        if (!sourceAssetId) {
          toast.error("El nodo Imagen conectado aún no ha generado su resultado. Falta la imagen de entrada.");
          return;
        }
        videoMode = "image_to_video";
      } else {
        // Sin conexión de nodo Imagen: comprobar si la toma vinculada ya tiene una imagen adecuada
        const sceneAsset = scene?.selectedAssetId ? assetById[scene.selectedAssetId] : null;
        if (sceneAsset && sceneAsset.type === "image") {
          sourceAssetId = sceneAsset.id;
          videoMode = "image_to_video";
        } else {
          if (prompt.includes("@startframe")) {
            toast.error("El prompt incluye '@startframe' pero este nodo Vídeo no tiene un nodo Imagen conectado como Start Frame.");
            return;
          }
          if (supportsT2V) {
            videoMode = "text_to_video";
            if (!prompt.trim()) {
              toast.error("Se requiere un prompt para generar video en modo texto.");
              return;
            }
          } else {
            toast.error(t.canvas.noSourceImage || "No hay una imagen fuente válida para generar video. Conecta un nodo Imagen o asigna una imagen en Storyboard.");
            return;
          }
        }
      }
    }

    // Ratio estructurado: del nodo (elección explícita), de la toma o del proyecto
    const projectRatio = project?.aspectRatio || project?.aspect_ratio || project?.format || "16:9";
    const sceneRatio = scene?.aspectRatio || scene?.aspect_ratio || scene?.format || null;
    const effectiveRatio = n.data?.aspect_ratio || sceneRatio || projectRatio;

    if (n.type === "video") {
      const modelDurations = Array.isArray(selectedModel?.durations) && selectedModel.durations.length > 0
        ? selectedModel.durations.map(Number)
        : (typeof selectedModel?.min_duration === "number" && typeof selectedModel?.max_duration === "number" && selectedModel.max_duration >= selectedModel.min_duration
            ? Array.from({ length: selectedModel.max_duration - selectedModel.min_duration + 1 }, (_, i) => selectedModel.min_duration + i)
            : []);
      if (modelDurations.length === 0) {
        toast.error(`El modelo ${selectedModel?.label || modelId} no tiene duraciones admitidas configuradas en el catálogo.`);
        return;
      }
      const chosenDuration = n.data?.duration != null && n.data?.duration !== "" ? Number(n.data.duration) : null;
      if (!chosenDuration || !modelDurations.includes(chosenDuration)) {
        toast.error(`Selecciona una duración válida admitida por ${selectedModel?.label || modelId} (${modelDurations.join(", ")} s).`);
        return;
      }
    }

    // Configuración de parámetros específicos por modelo (incluyendo soporte de audio nativo)
    const nodeAudio = n.data?.audio != null ? Boolean(n.data.audio) : (selectedModel?.default_audio || false);
    const effectiveDuration = n.data?.duration != null && n.data?.duration !== "" ? Number(n.data.duration) : 5;
    const params = n.type === "video"
      ? (modelId === "kling-v3" || modelId?.startsWith("kling-")
          ? { video_mode: videoMode, mode: nodeAudio ? "pro" : "std", duration: effectiveDuration, audio: nodeAudio, aspect_ratio: effectiveRatio, end_asset_id: videoMode === "first_last_frame" ? endAssetId : undefined }
          : { video_mode: videoMode, resolution: (selectedModel?.resolutions?.find((r) => r.toLowerCase() === (n.data?.resolution || "").toLowerCase()) || n.data?.resolution || "1080P"), duration: effectiveDuration, audio: selectedModel?.audio_supported ? nodeAudio : undefined, aspect_ratio: effectiveRatio, end_asset_id: videoMode === "first_last_frame" ? endAssetId : undefined })
      : {
          aspect_ratio: effectiveRatio,
          resolution: n.data?.resolution || (selectedModel?.resolutions ? selectedModel.resolutions[0] : undefined),
        };

    // Referencias visuales asignadas realmente:
    // 1. Escena vinculada (excluyendo las excluidas explícitamente en el nodo)
    const excludedSceneRefIds = new Set(n.data?.excluded_scene_ref_ids || []);
    const sceneRefAssets = (scene?.referenceIds || [])
      .filter((rid) => !excludedSceneRefIds.has(rid))
      .map((rid) => {
        const ref = referenceById[rid] || state.references?.find((r) => r.id === rid);
        return ref?.assetId;
      })
      .filter(Boolean);

    // 2. Nodo (añadidas explícitamente al nodo)
    const nodeRefAssets = (n.data?.reference_ids || n.data?.referenceIds || [])
      .map((rid) => {
        const ref = referenceById[rid] || state.references?.find((r) => r.id === rid);
        return ref?.assetId;
      })
      .filter(Boolean);

    // 3. Conexiones de nodos referencia en el lienzo
    const canvasRefAssets = inputs
      .filter((i) => i.type === "reference")
      .map((i) => {
        const ref = referenceById[i.data.referenceId] || state.references?.find((r) => r.id === i.data.referenceId);
        return ref?.assetId;
      })
      .filter(Boolean);

    const allRefAssetIds = Array.from(new Set([...sceneRefAssets, ...nodeRefAssets, ...canvasRefAssets]));

    // Validación estricta según el modelo sin descartar silenciosamente
    const modelSupportsVisualRefs = Boolean(
      selectedModel?.supports_references === true ||
      (selectedModel?.veyra_max_references != null && selectedModel.veyra_max_references > 0) ||
      selectedModel?.reference_supported === true
    );
    const maxRefs = selectedModel?.max_references ?? selectedModel?.maxReferences ?? (modelSupportsVisualRefs ? 2 : 0);
    if (!modelSupportsVisualRefs && allRefAssetIds.length > 0) {
      toast.error(`El modelo ${selectedModel?.label || modelId} no admite referencias visuales adicionales.`);
      return;
    }
    if (allRefAssetIds.length > maxRefs) {
      toast.error(`Se han asignado ${allRefAssetIds.length} referencias visuales, pero el modelo ${selectedModel?.label || modelId} admite un máximo de ${maxRefs}. Quita referencias para poder ejecutar.`);
      return;
    }

    const payload = {
      idempotency_key: uuidv4(),
      provider_id: providerId,
      model_id: modelId,
      kind: n.type === "video" ? "video" : "image",
      project_id: project.id,
      scene_id: scene?.id || null,
      node_id: n.id,
      prompt,
      reference_asset_ids: allRefAssetIds,
      ...(n.type === "video" && {
        source_asset_id: (videoMode === "image_to_video" || videoMode === "first_last_frame") ? (sourceAssetId || null) : null,
        end_asset_id: videoMode === "first_last_frame" ? (endAssetId || null) : null,
      }),
      params,
    };

    if (n.type === "video") {
      const startObj = sourceAssetId ? (nodes.find((x) => x.id === sourceAssetId) ? resolveNodeAsset(nodes.find((x) => x.id === sourceAssetId), data, assetById) : (assetById[sourceAssetId] || null)) : null;
      const endObj = endAssetId ? (nodes.find((x) => x.id === endAssetId) ? resolveNodeAsset(nodes.find((x) => x.id === endAssetId), data, assetById) : (assetById[endAssetId] || null)) : null;
      setCanvasPreflight({
        payload,
        model: selectedModel,
        videoMode,
        startAsset: startObj,
        endAsset: endObj,
        duration: params.duration || 5,
        resolution: params.resolution || "1080P",
        aspectRatio: effectiveRatio,
        audio: Boolean(params.audio),
        cost: selectedModel?.rate_per_second_usd ? (params.duration * selectedModel.rate_per_second_usd * 1.2) : (selectedModel?.reserve_usd || null),
        prompt,
        nodeId: n.id,
      });
      return;
    }

    try {
      const job = await submitGeneration(payload);
      setPending(true);
      setPendingNodeId(n.id);
      setPendingJobId(job.job_id);
    } catch (err) {
      toast.error(err.message || t.generation?.submitError || "Error al enviar la generación");
      setPending(false);
    }
  };

  const confirmCanvasPreflight = async () => {
    if (!canvasPreflight) return;
    const { payload, nodeId } = canvasPreflight;
    setCanvasPreflight(null);
    try {
      const job = await submitGeneration(payload);
      setPending(true);
      setPendingNodeId(nodeId);
      setPendingJobId(job.job_id);
      toast.success(
        <span>
          {t.generation?.submittedToast || "Generación enviada."}{" "}
          {job.job_id && <span className="font-mono text-xs opacity-75">({job.job_id.slice(0, 8)})</span>}
        </span>
      );
    } catch (err) {
      toast.error(err.message || t.generation?.submitError || "Error al enviar la generación");
      setPending(false);
    }
  };
// duplicate generation block removed


















        // Verificar que el modelo tenga modos compatibles (texto->video o imagen->video)





























































  const onWheel = (e) => { const k = Math.min(2, Math.max(0.4, view.k * (e.deltaY < 0 ? 1.08 : 0.92))); setView((v) => ({ ...v, k })); };
  const pos = (n) => (drag?.id === n.id ? { x: n.x + drag.dx, y: n.y + drag.dy } : { x: n.x, y: n.y });
  const selectedEdge = edges.find((e) => e.id === selection.edgeId);

  return (
    <div data-testid="canvas-view" className="relative flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-[#303030] bg-[#141414] px-3 py-2">
        <span className="v-eyebrow mr-1">{t.canvas.addNode}</span>
        {NODE_TYPES.map((ty) => <button key={ty} data-testid={`canvas-add-${ty}`} className="v-btn-ghost v-btn-sm" onClick={() => addNode(ty)}><Plus size={12} /><span className="h-2 w-2 rounded-full" style={{ background: TYPE_COLOR[ty] }} />{t.canvas.nodeTypes[ty]}</button>)}
        <span className="mx-1 hidden h-5 w-px bg-[#303030] sm:block" />
        {selectedEdge && (
          <div className="flex items-center gap-1.5">
            {(() => {
              const targetNode = nodes.find((n) => n.id === selectedEdge.targetId);
              const sourceNode = nodes.find((n) => n.id === selectedEdge.sourceId);
              const isVideoInput = targetNode?.type === "video" && sourceNode?.type === "image";
              if (!isVideoInput) return null;
              return (
                <div className="flex items-center gap-1 bg-[#1e1e1e] px-2 py-0.5 rounded border border-[#303030] text-xs">
                  <span className="text-[#a3a3a3] mr-1">Rol:</span>
                  <button
                    type="button"
                    data-testid="edge-role-start"
                    className={`px-2 py-0.5 rounded text-[11px] font-medium transition ${selectedEdge.role === "start_frame" ? "bg-[#34d399]/20 text-[#34d399] border border-[#34d399]/40" : "text-[#888] hover:text-white"}`}
                    onClick={() => dispatch({ type: "EDGE_UPDATE", payload: { id: selectedEdge.id, patch: { role: "start_frame" } } })}
                  >
                    Start Frame
                  </button>
                  <button
                    type="button"
                    data-testid="edge-role-end"
                    className={`px-2 py-0.5 rounded text-[11px] font-medium transition ${selectedEdge.role === "end_frame" ? "bg-[#f87171]/20 text-[#f87171] border border-[#f87171]/40" : "text-[#888] hover:text-white"}`}
                    onClick={() => dispatch({ type: "EDGE_UPDATE", payload: { id: selectedEdge.id, patch: { role: "end_frame" } } })}
                  >
                    End Frame
                  </button>
                </div>
              );
            })()}
            <button data-testid="canvas-delete-edge" className="v-btn-ghost v-btn-sm text-[#e5655a]" onClick={() => dispatch({ type: "EDGE_DELETE", payload: { id: selectedEdge.id } })}><Unlink size={12} />{t.canvas.deleteEdge}</button>
          </div>
        )}
        {selection.nodeId && !selectedEdge && (
          <div className="flex items-center gap-1.5">
            <button
              data-testid="canvas-configure-node"
              className="v-btn-primary v-btn-sm"
              onClick={() => setConfigNodeId(selection.nodeId)}
            >
              <SlidersHorizontal size={12} />
              Configurar nodo
            </button>
            <button
              data-testid="canvas-delete-node"
              className="v-btn-ghost v-btn-sm text-[#e5655a]"
              onClick={() => dispatch({ type: "NODE_DELETE", payload: { id: selection.nodeId } })}
            >
              <Trash2 size={12} />
              {t.canvas.deleteNode}
            </button>
          </div>
        )}
        <div className="ml-auto flex items-center gap-1 text-xs text-[#a3a3a3]">
          <span data-testid="canvas-counts" className="mr-2 font-mono">{t.canvas.nodes(nodes.length)} · {t.canvas.edges(edges.length)}</span>
          <button data-testid="canvas-zoom-out" className="rounded p-1 hover:bg-[#222]" onClick={() => setView((v) => ({ ...v, k: Math.max(0.4, v.k * 0.9) }))} aria-label={t.canvas.zoom}><ZoomOut size={14} /></button>
          <span className="w-10 text-center font-mono">{Math.round(view.k * 100)}%</span>
          <button data-testid="canvas-zoom-in" className="rounded p-1 hover:bg-[#222]" onClick={() => setView((v) => ({ ...v, k: Math.min(2, v.k * 1.1) }))} aria-label={t.canvas.zoom}><ZoomIn size={14} /></button>
          <button data-testid="canvas-fit" className="rounded p-1 hover:bg-[#222]" onClick={() => setView({ x: 40, y: 40, k: 1 })} aria-label={t.canvas.fit}><Maximize size={14} /></button>
        </div>
      </div>

      <div ref={wrapRef} data-testid="canvas-stage" className={`v-grid-bg relative min-h-[460px] flex-1 overflow-hidden select-none md:min-h-0 ${pan ? "cursor-grabbing" : "cursor-grab"}`} onWheel={onWheel}
        onPointerDown={(e) => { if (e.target === e.currentTarget || e.target.tagName === "svg") { setPan({ sx: e.clientX, sy: e.clientY, ox: view.x, oy: view.y }); dispatch({ type: "SELECT", payload: { projectId: project.id, patch: { nodeId: null, edgeId: null } } }); } }}>
        {nodes.length === 0 && <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-8"><div className="pointer-events-auto w-full max-w-md"><EmptyState title={t.canvas.empty} body={t.canvas.emptyBody} testId="canvas-empty" /></div></div>}
        <div className="absolute left-0 top-0 origin-top-left" style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})` }}>
          <svg className="absolute left-0 top-0 overflow-visible" width="1" height="1" style={{ pointerEvents: "none" }}>
            {edges.map((e) => {
              const a = nodes.find((n) => n.id === e.sourceId), b = nodes.find((n) => n.id === e.targetId);
              if (!a || !b) return null;
              const d = edgePath(pos(a), pos(b));
              const sel = e.id === selection.edgeId;
              const isVideoInput = b.type === "video" && a.type === "image";
              const strokeColor = sel
                ? "#f5b544"
                : isVideoInput
                  ? (e.role === "start_frame" ? "#34d399" : e.role === "end_frame" ? "#f87171" : "#5a5a5a")
                  : "#5a5a5a";
              const midX = (pos(a).x + NODE_W + pos(b).x) / 2;
              const midY = (pos(a).y + PORT_Y + pos(b).y + PORT_Y) / 2;
              return (
                <g key={e.id} data-testid={`canvas-edge-${e.id}`} style={{ pointerEvents: "all", cursor: "pointer" }} onPointerDown={(ev) => { ev.stopPropagation(); dispatch({ type: "SELECT", payload: { projectId: project.id, patch: { edgeId: e.id, nodeId: null } } }); }}>
                  <path d={d} fill="none" stroke="transparent" strokeWidth={16} />
                  <path d={d} fill="none" stroke={strokeColor} strokeWidth={sel ? 2.5 : 1.75} strokeDasharray={isVideoInput && e.role === "end_frame" ? "5 3" : undefined} />
                  {isVideoInput && e.role && (
                    <g transform={`translate(${midX}, ${midY})`} className="pointer-events-none select-none">
                      <rect x="-34" y="-9" width="68" height="18" rx="4" fill="#141414" stroke={e.role === "start_frame" ? "#34d399" : "#f87171"} strokeWidth="1" />
                      <text x="0" y="3.5" textAnchor="middle" fill={e.role === "start_frame" ? "#34d399" : "#f87171"} fontSize="9" fontWeight="600" fontFamily="sans-serif">
                        {e.role === "start_frame" ? "Start Frame" : "End Frame"}
                      </text>
                    </g>
                  )}
                </g>
              );
            })}
            {link && (() => { const a = nodes.find((n) => n.id === link.sourceId); return a ? <path d={edgePath(pos(a), { x: link.x, y: link.y - PORT_Y })} fill="none" stroke="#f5b544" strokeDasharray="6 4" strokeWidth={1.75} /> : null; })()}
          </svg>

          {nodes.map((n) => {
            const p = pos(n);
            const isSel = selection.nodeId === n.id;
            const isSceneSel = n.data.sceneId && n.data.sceneId === selection.sceneId;
            return (
              <div key={n.id} data-testid={`canvas-node-${n.id}`} data-node-type={n.type} className={`absolute rounded-xl border bg-[#1b1b1b] shadow-[0_8px_30px_rgba(0,0,0,0.45)] transition-[border-color,box-shadow] ${isSel ? "border-[#f5b544] shadow-[0_0_0_1px_#f5b544]" : isSceneSel ? "border-[#8a6a2a]" : "border-[#303030]"}`} style={{ left: p.x, top: p.y, width: NODE_W }} onPointerDown={(e) => { e.stopPropagation(); selectNode(n); }}>
                <div data-testid={`canvas-node-handle-${n.id}`} className="flex h-10 cursor-move items-center gap-2 border-b border-[#262626] px-3" onPointerDown={(e) => { e.stopPropagation(); selectNode(n); setDrag({ id: n.id, sx: e.clientX, sy: e.clientY, ox: n.x, oy: n.y, dx: 0, dy: 0 }); }}>
                  <span className="h-2 w-2 rounded-full" style={{ background: TYPE_COLOR[n.type] }} />
                  <span className="text-xs font-semibold">{t.canvas.nodeTypes[n.type]}</span>
                  {n.data.sceneId && <span className="truncate font-mono text-[10px] text-[#a3a3a3]">· {data.scenes.findIndex((s) => s.id === n.data.sceneId) + 1}</span>}
                  {n.id.startsWith("nod_demo") && <DemoBadge className="ml-auto" />}
                  {n.data.model_id && (() => {
                    const list = n.type === "image" ? imageModels : videoModels;
                    const m = list.find((mod) => mod.id === n.data.model_id);
                    return <span className="ml-2 text-xs text-[#a3a3a3]">{m ? m.label || m.id : n.data.model_id}</span>;
                  })()}
                  <button
                    type="button"
                    data-testid={`canvas-node-config-${n.id}`}
                    title="Configurar nodo"
                    className="ml-auto rounded p-1 hover:bg-[#303030] text-[#a3a3a3] hover:text-[#f5b544] transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      selectNode(n);
                      setConfigNodeId(n.id);
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    <SlidersHorizontal size={13} />
                  </button>
                </div>
                {hasInputs(n.type) && <span data-port-in={n.id} data-testid={`canvas-port-in-${n.id}`} className="absolute -left-2 top-3 h-4 w-4 rounded-full border-2 border-[#a3a3a3] bg-[#101010] hover:border-[#f5b544]" style={{ cursor: "crosshair" }} onPointerDown={(e) => e.stopPropagation()} />}
                {hasOutput(n.type) && <span data-testid={`canvas-port-out-${n.id}`} className="absolute -right-2 top-3 h-4 w-4 rounded-full border-2 border-[#f5b544] bg-[#f5b544] hover:scale-110" style={{ cursor: "crosshair" }} onPointerDown={(e) => { e.stopPropagation(); selectNode(n); const c = toCanvas(e.clientX, e.clientY); setLink({ sourceId: n.id, x: c.x, y: c.y }); }} />}
                <div className="p-2.5">
                  <NodeBody
                    node={n}
                    data={data}
                    onRun={() => run(n)}
                    hasOperational={hasOperational}
                    hasConnection={hasConnection}
                    onOpenConfig={() => setConfigNodeId(n.id)}
                    onAssignScene={(nodeObj, assetObj, sceneObj) => {
                      if (sceneObj.selectedAssetId && sceneObj.selectedAssetId !== assetObj.id) {
                        setReplaceTarget({ scene: sceneObj, asset: assetObj });
                      } else {
                        dispatch({
                          type: "SCENE_SELECT_ASSET",
                          payload: { sceneId: sceneObj.id, assetId: assetObj.id },
                        });
                        dispatch({
                          type: "SELECT",
                          payload: {
                            projectId: project.id,
                            patch: { sceneId: sceneObj.id, assetId: assetObj.id },
                          },
                        });
                        toast.success("Variante asignada como medio activo de la toma.");
                      }
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <div className="pointer-events-none absolute bottom-3 left-3 max-w-md rounded-md bg-[#101010]/80 px-2.5 py-1.5 text-[11px] text-[#a3a3a3] backdrop-blur" data-testid="canvas-hint">{t.canvas.hint}</div>
      </div>
      <PendingDialog open={pending} onOpenChange={setPending} phase={3} registered extra={t.canvas.runHint} />
      <VideoPreflightModal
        open={Boolean(canvasPreflight)}
        onClose={() => setCanvasPreflight(null)}
        onConfirm={confirmCanvasPreflight}
        model={canvasPreflight?.model}
        videoMode={canvasPreflight?.videoMode}
        startAsset={canvasPreflight?.startAsset}
        endAsset={canvasPreflight?.endAsset}
        duration={canvasPreflight?.duration}
        resolution={canvasPreflight?.resolution}
        aspectRatio={canvasPreflight?.aspectRatio}
        audio={canvasPreflight?.audio}
        cost={canvasPreflight?.cost}
        prompt={canvasPreflight?.prompt}
      />
      {Boolean(configNodeId) && (
        <NodeConfigModal
          open={Boolean(configNodeId)}
          onClose={() => setConfigNodeId(null)}
          node={nodes.find((n) => n.id === configNodeId) || null}
          data={data}
          onRun={(updatedNode) => {
            const targetNode = updatedNode || nodes.find((n) => n.id === configNodeId);
            if (targetNode) run(targetNode);
          }}
          hasOperational={hasOperational}
        />
      )}
      <ConfirmDialog
        open={Boolean(replaceTarget)}
        onOpenChange={(open) => {
          if (!open) setReplaceTarget(null);
        }}
        title="¿Reemplazar medio activo de la toma?"
        body={`La toma "${replaceTarget?.scene?.title || "Toma"}" ya tiene un medio asignado. Al asignar esta variante, el medio anterior se conservará en la biblioteca y en el historial.`}
        confirmLabel="Reemplazar medio activo"
        destructive={false}
        onConfirm={() => {
          if (replaceTarget?.scene && replaceTarget?.asset) {
            dispatch({
              type: "SCENE_SELECT_ASSET",
              payload: { sceneId: replaceTarget.scene.id, assetId: replaceTarget.asset.id },
            });
            dispatch({
              type: "SELECT",
              payload: {
                projectId: project.id,
                patch: { sceneId: replaceTarget.scene.id, assetId: replaceTarget.asset.id },
              },
            });
            toast.success("Variante asignada como medio activo de la toma.");
            setReplaceTarget(null);
          }
        }}
      />
    </div>
  );
};

export default CanvasView;
